import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ModalController } from '@ionic/angular';
import { MenuItem } from 'primeng/api';
import { ContextMenu } from 'primeng/contextmenu';
import { ApiService } from 'src/app/api/api.service';
import { GlobalService } from 'src/app/api/global.service';
import { CreateComponent as GroupCreateComponent } from '../../groups/create/create.component'
import { SelectComponent as GroupSelectComponent } from '../../groups/select/select.component';
import * as dayjs from 'dayjs';

@Component({
  selector: 'app-template-book',
  templateUrl: './template-book.component.html',
  styleUrls: ['./template-book.component.scss'],
})
export class TemplateBookComponent implements OnInit {
  @Input() template_select_mode = false;
  @Input() max_height: number = 350;
  @Input() hide_repeat_rules = false;
  @Input() max_selection: number = 1; // Maximum number of templates that can be selected
  @Input() selected_templates: any[] = []; // Pre-selected templates
  templates: any[] = []; // Your templates from API
  template_groups: any[] = []; // Your template groups from API
  accountant_templates: any[] = []; // Our templates from JSON
  accountant_template_groups: any[] = []; // Our template groups from JSON
  highlighted_template: any = null;
  expanded_groups: string[] = []; // Track expanded group GUIDs
  @Input() edit_select_mode = false;

  @Input() external_selection_mode = false;
  @Input() expanded_your_templates = true;
  @Input() expanded_our_templates = false;
  // Leave out "Our Templates" (the ready-made ones we supply). Used when picking a template to change in place - only the firm's own
  // templates can be edited.
  @Input() hide_our_templates = false;

  hide_empty_groups = false;
  non_grouped_your_templates = 0;
  non_grouped_our_templates = 0;
  non_grouped_your_templates_expanded = false;
  non_grouped_our_templates_expanded = false;

  template_context_items: MenuItem[] | undefined;
  group_context_items: MenuItem[] | undefined;
  @ViewChild('template_context_menu') template_context_menu: ContextMenu;
  @ViewChild('group_context_menu') group_context_menu: ContextMenu;

  current_context_template: any = null;
  current_context_group: any = null;
  loading_template = false;

  @Output() highlightChanged = new EventEmitter<{ data: any }>();

  constructor(
    private apiService: ApiService,
    private modalController: ModalController,
    public globalService: GlobalService,
    private http: HttpClient
  ) { }

  ngOnInit() {
    this.template_context_items = [
      {
        label: 'Edit Template',
        icon: 'fa-solid fa-edit',
        command: () => {
          this.EditTemplate(this.current_context_template);
        }
      },
      {
        label: 'Delete Template',
        icon: 'fa-solid fa-trash',
        command: () => {
          this.DeleteTemplate(this.current_context_template);
        }
      },
      {
        label: 'Manage Groups',
        icon: 'fa-solid fa-people-group',
        command: () => {
          this.ManageTemplateGroups(this.current_context_template);
        }
      }
    ];

    this.group_context_items = [
      {
        label: 'Edit Group',
        icon: 'fa-solid fa-edit',
        command: () => {
          this.UpdateGroup(this.current_context_group);
        }
      },
      {
        label: 'Add Subgroup',
        icon: 'fa-solid fa-plus',
        command: () => {
          this.AddNewGroup(this.current_context_group);
        }
      },
      {
        label: 'Delete Group',
        icon: 'fa-solid fa-trash',
        command: () => {
          this.DeleteGroup(this.current_context_group);
        }
      },
      {
        separator: true
      },
      {
        label: 'Move all templates to another group',
        icon: 'fa-solid fa-circle-info',
        command: () => {
          this.MoveToAnotherGroup(this.current_context_group, true);
        }
      },
      {
        label: 'Copy all templates to another group',
        icon: 'fa-solid fa-circle-info',
        command: () => {
          this.MoveToAnotherGroup(this.current_context_group, false);
        }
      },
      {
        label: 'Remove all templates from group',
        icon: 'fa-solid fa-circle-info',
        command: () => {
          this.RemoveFromGroup(this.current_context_group, true);
        }
      },
    ];

    this.GetData();
  }

  async GetData() {
    // Save currently expanded groups
    this.expanded_groups = [];
    this.template_groups.forEach(group => {
      if (group.expanded) this.expanded_groups.push(group.guid);
    });
    this.accountant_template_groups.forEach(group => {
      if (group.expanded) this.expanded_groups.push(group.guid);
    });

    await this.GetTemplates();
    await this.GetTemplateGroups();
    await this.GetAccountantTemplates();
    await this.GetAccountantTemplateGroups();
    this.CombineData();
    this.ShowLastExpanded();
  }

  async GetTemplates() {
    let data: any = await this.apiService.get('templates/projects/quick');
    if (data.success) {
      this.templates = data.data || [];
      // Parse JSON strings if needed
      this.templates.forEach(template => {
        if (typeof template.groups_str === 'string') {
          try {
            template.tasks = [];
            template.tags = [];
            template.groups = JSON.parse(template.groups_str) || [];
            template.repeat_rules = [];
          } catch (e) {
            console.error('Error parsing template JSON:', e);
          }
        }
      });
    }
  }

  async GetTemplateByGUID(guid: string) {
    this.loading_template = true;
    const existingTemplateIndex = this.templates.findIndex(t => t.guid === guid);
    if (existingTemplateIndex === -1) {
      this.loading_template = false;
      return;
    }
    
    const existingTemplate = this.templates[existingTemplateIndex];
    if(!existingTemplate.tasks) existingTemplate.tasks = [];
    if (existingTemplate.tasks.length == 0) {

      let data: any = await this.apiService.get(`templates/projects/${guid}`);
      if (data.success) {
        const fetchedTemplate = data.data[0];
        
        const parsed = JSON.parse(fetchedTemplate.json_str);
        fetchedTemplate.tasks = parsed.tasks || [];
        fetchedTemplate.tags = parsed.tags || [];
        fetchedTemplate.groups = parsed.groups || [];
        fetchedTemplate.repeat_rules = parsed.repeat_rules || [];

        // Update the template in the array by replacing it at the same index
        this.templates[existingTemplateIndex] = fetchedTemplate;
      }
    }
    this.loading_template = false;
  }

  async GetTemplateGroups() {
    let data: any = await this.apiService.GetGroups('projects');
    if (data.success) {
      this.template_groups = data.data || [];
      // Sort template groups alphabetically by title
      this.template_groups.sort((a, b) => {
        const titleA = (a.title || '').toLowerCase();
        const titleB = (b.title || '').toLowerCase();
        return titleA.localeCompare(titleB);
      });
    }
  }

  async GetAccountantTemplates() {
    try {
      // Load accountant project templates from dedicated JSON file
      const data: any = await this.http.get('assets/keyfiles/Accountants/project_templates.json').toPromise();

      if (data && Array.isArray(data)) {
        this.accountant_templates = data.map(template => ({
          ...template,
          is_firm_wide: 1, // Mark as "Our Templates"
          tasks: template.tasks || [],
          tags: template.tags || [],
          groups: template.groups || [],
          repeat_rules: template.repeat_rules || [],
          json_str: JSON.stringify(template)
        }));
      } else {
        this.accountant_templates = [];
      }

    } catch (error) {
      console.error('Error loading accountant templates:', error);
      this.accountant_templates = [];
    }
  }

  async GetAccountantTemplateGroups() {
    try {
      // Load accountant project groups from dedicated JSON file
      const data: any = await this.http.get('assets/keyfiles/Accountants/project_groups.json').toPromise();

      if (data && Array.isArray(data)) {
        this.accountant_template_groups = data.map(group => ({
          ...group,
          templates: [],
          expanded: false,
          is_firm_wide: 1 // Mark as "Our Templates"
        }));
      } else {
        this.accountant_template_groups = [];
      }

      // Sort accountant template groups alphabetically by title
      this.accountant_template_groups.sort((a, b) => {
        const titleA = (a.title || '').toLowerCase();
        const titleB = (b.title || '').toLowerCase();
        return titleA.localeCompare(titleB);
      });
    } catch (error) {
      console.error('Error loading accountant template groups:', error);
      this.accountant_template_groups = [];
    }
  }

  CombineData() {
    this.non_grouped_your_templates = 0;
    this.non_grouped_our_templates = 0;

    // Initialize template arrays for YOUR template groups (from API)
    this.template_groups.forEach(group => {
      group.templates = [];
      group.expanded = false;
      group.is_firm_wide = 0; // Mark as "Your Templates"
    });

    // Initialize template arrays for ACCOUNTANT template groups (from JSON)
    this.accountant_template_groups.forEach(group => {
      group.templates = [];
      group.expanded = false;
      group.is_firm_wide = 1; // Mark as "Our Templates"
    });

    // Assign YOUR templates to their groups
    this.templates.forEach(template => {
      template.is_firm_wide = 0; // Mark as "Your Templates"
      if (!template.groups || template.groups.length === 0) {
        this.non_grouped_your_templates++;
      } else {
        // Add template to each of its groups
        template.groups.forEach(templateGroup => {
          const group = this.template_groups.find(g => g.guid === templateGroup.guid);
          if (group && !group.templates.find(t => t.guid === template.guid)) {
            group.templates.push(template);
          }
        });
      }
    });

    // Assign ACCOUNTANT templates to their groups
    this.accountant_templates.forEach(template => {
      template.is_firm_wide = 1; // Mark as "Our Templates"
      template.repeat_rules.repeat_start_date = dayjs().format('YYYY-MM-DD');
      if (!template.groups || template.groups.length === 0) {
        this.non_grouped_our_templates++;
      } else {
        // Add template to each of its groups
        template.groups.forEach(templateGroup => {
          const group = this.accountant_template_groups.find(g => g.guid === templateGroup.guid);
          if (group && !group.templates.find(t => t.guid === template.guid)) {
            group.templates.push(template);
          }
        });
      }
    });

    // Update parent group counts for both sets
    this.UpdateGroupsCount();
  }

  UpdateGroupsCount() {
    // Update counts for YOUR template groups
    if (this.template_groups) {
      for (const group of this.template_groups) {
        this.UpdateParentGroupsCount(group, this.template_groups);
      }
    }

    // Update counts for ACCOUNTANT template groups
    if (this.accountant_template_groups) {
      for (const group of this.accountant_template_groups) {
        this.UpdateParentGroupsCount(group, this.accountant_template_groups);
      }
    }
  }

  UpdateParentGroupsCount(group, groupsArray) {
    if (!group.parent_guid) return;

    const parent_group = groupsArray.find(g => g.guid === group.parent_guid);
    if (parent_group) {
      for (const template of group.templates) {
        if (!parent_group.templates.some(t => t.guid === template.guid)) {
          parent_group.templates.push(template);
        }
      }
      this.UpdateParentGroupsCount(parent_group, groupsArray);
    }
  }

  ShowLastExpanded() {
    // Restore expanded state for groups that were previously expanded
    if (this.expanded_groups.length === 0) return;

    this.template_groups.forEach(group => {
      if (this.expanded_groups.includes(group.guid)) {
        group.expanded = true;
      }
    });

    this.accountant_template_groups.forEach(group => {
      if (this.expanded_groups.includes(group.guid)) {
        group.expanded = true;
      }
    });
  }

  yourTemplatesCount(): number {
    return this.templates.length;
  }

  ourTemplatesCount(): number {
    return this.accountant_templates.length;
  }

  async highlightTemplate(guid, isYourTemplates) {
    if(isYourTemplates) await this.GetTemplateByGUID(guid);
    let template: any = null;
    if(isYourTemplates)template = this.templates.find(t => t.guid === guid);
    else template = this.accountant_templates.find(t => t.guid === guid);
    // For single selection
    if (this.max_selection === 1) {
      this.highlighted_template = template;
      this.highlightChanged.emit({ data: template });
      return;
    }

    // For multiple selection
    if (!this.selected_templates) {
      this.selected_templates = [];
    }

    const index = this.selected_templates.findIndex(t => t.guid === template.guid);
    if (index > -1) {
      // Deselect
      this.selected_templates.splice(index, 1);
    } else {
      // Select
      this.selected_templates.push(template);
    }

    // Update highlighted template to the last selected one
    if (this.selected_templates.length > 0) {
      this.highlighted_template = this.selected_templates[this.selected_templates.length - 1];
    } else {
      this.highlighted_template = null;
    }

    this.highlighted_template.is_your_template = isYourTemplates;

    this.highlightChanged.emit({ data: this.selected_templates });
  }

  IsTemplateSelected(template: any): boolean {
    if (this.max_selection === 1) {
      return this.highlighted_template?.guid === template.guid;
    }
    return this.selected_templates && this.selected_templates.some(t => t.guid === template.guid);
  }

  onContextMenuTemplate(event: MouseEvent, template: any) {
    event.preventDefault();
    this.current_context_template = template;
    this.template_context_menu.show(event);
  }

  onContextMenuGroup(event: MouseEvent, group: any) {
    event.preventDefault();
    this.current_context_group = group;
    this.group_context_menu.show(event);
  }

  async OpenCreateTemplateModal() {
    // TODO: Implement create template modal
    this.globalService.presentToastMessage('Create template functionality coming soon', true);
  }

  async EditTemplate(template: any) {
    // TODO: Implement edit template modal
    this.globalService.presentToastMessage('Edit template functionality coming soon', true);
  }

  async DeleteTemplate(template: any) {
    if (!template) return;
    const confirmation = window.prompt(`Are you sure you want to delete "${template.title}"? This action cannot be undone. Please type the template title to confirm:`);
    if (confirmation !== template.title) {
      this.globalService.presentToastMessage('Deletion cancelled', false);
      return;
    }
    let data: any = await this.apiService.delete(`templates/projects/${template.guid}`, null);
    if (data.success) {
      const index = this.templates.findIndex(t => t.guid === template.guid);
      if (index > -1) {
        this.templates.splice(index, 1);
      }
      this.GetData();
    }
    this.globalService.presentToastMessage(data.message, data.success);
  }

  async ManageTemplateGroups(template: any) {
    // TODO: Implement manage groups modal
    this.globalService.presentToastMessage('Manage groups functionality coming soon', true);
  }

  async OpenManageGroups() {
    // TODO: Implement manage groups modal
    this.globalService.presentToastMessage('Manage groups functionality coming soon', true);
  }

  async UpdateGroup(group: any) {
    // TODO: Implement update group modal
    this.globalService.presentToastMessage('Update group functionality coming soon', true);
  }

  async AddNewGroup(parentGroup: any) {
    const modal = await this.modalController.create({
      component: GroupCreateComponent,
      cssClass: 'medium-modal secondary-modal',
      backdropDismiss: false,
      componentProps: {
        url: 'projects',
        parent_group: parentGroup,
        edit_mode: false
      }
    });

    modal.onDidDismiss().then(async (data: any) => {
      if (data && data.data) {
        this.GetData();
      }
    });

    return await modal.present();
  }

  async DeleteGroup(group: any) {
    if (!group) return;
    if (group.templates && group.templates.length > 0) {
      this.globalService.presentToastMessage('Cannot delete a group that has templates assigned to it. Please move the templates to a different group first.', false);
      return;
    }
    let data: any = await this.apiService.delete(`groups/projects/${group.guid}/false`, null);
    if (data.success) {
      this.GetData();
    }
    this.globalService.presentToastMessage(data.message, data.success);
  }

  GenerateTemplateTooltip(template: any): string {
    let tooltip = `<strong>${template.title}</strong><br>`;
    tooltip += template.description ? `${template.description}<br>` : '';
    tooltip += `Tasks: ${template.tasks?.length || 0}<br>`;
    return tooltip;
  }

  async Close() {
    this.modalController.dismiss();
  }

  async Select() {
    if (this.max_selection === 1) {
      if (this.highlighted_template) {
        this.modalController.dismiss(this.highlighted_template);
      }
    } else {
      this.modalController.dismiss(this.selected_templates);
    }
  }

  async MoveToAnotherGroup(group, delete_from_current) {
    if (!group) return;
    if (group.templates.length == 0) {
      this.globalService.presentToastMessage('This group must have templates in it before moving these templates to another group.', false);
      return;
    }
    const modal = await this.modalController.create({
      component: GroupSelectComponent,
      cssClass: 'medium-modal secondary-modal',
      backdropDismiss: false,
      componentProps: {
        url: 'projects',
      }
    });

    modal.onDidDismiss().then(async (data: any) => {
      if (data && data.data) {
        let new_groups = data.data;
        await this.globalService.presentLoading("Updating Groups", 999999);
        // // Refresh company groups and recombine data after a new subgroup is created
        // await this.GetCompanyGroups();
        // this.CombinedData();
        // this.ExpandAllParentGroups(group.guid);
        if (delete_from_current) {
          this.RemoveFromGroup(group, false);
        }
        //Loop through the selected groups and add the companies/clients to the new group
        for (let i = 0; i < new_groups.length; i++) {
          const new_group = new_groups[i];

          for (let j = 0; j < group.templates.length; j++) {
            let template = group.templates[j];
            await this.globalService.presentLoading(`Adding ${template.name} to ${new_group.title}`, 999999);
            await this.AddTemplateToGroup(template, new_group);
          }
        }
        await this.GetData();
        await this.globalService.dismissLoading();
      }
    });
    return await modal.present();
  }

  async RemoveFromGroup(group, confirm) {
    if (!group) return;
    if (group.templates.length == 0) {
      this.globalService.presentToastMessage('This group must have templates in it before moving these templates to another group.', false);
      return;
    }
    if (confirm) {
      const groupName = group && group.name ? ` "${group.name}"` : '';
      const confirmation = window.prompt(`Are you sure you want to remove all templates from the group ${groupName}? This action cannot be undone. Please type "Remove" to confirm:`);
      if (confirmation !== 'Remove') {
        this.globalService.presentToastMessage('Operation cancelled', false);
        return;
      }
    }
    if (confirm) await this.globalService.presentLoading("Removing from Group", 999999);
    for (let i = 0; i < group.templates.length; i++) {
      let template = group.templates[i];
      await this.RemoveTemplateFromGroup(template, group);
    }
    await this.GetData();
    if (confirm) await this.globalService.dismissLoading();
    return true;
  }



  async AddTemplateToGroup(template, group) {
    let data: any = await this.apiService.post(`groups/project_templates/${template.guid}`, group);
    return data;
  }

  async RemoveTemplateFromGroup(template, group) {
    let data: any = await this.apiService.delete(`groups/project_templates/${template.guid}`, group);
    return data;
  }





}
